import { CircleCheck, CircleAlert, Loader2, Circle, CircleSlash, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  usePaperProcessingStatus,
  STEP_LABELS,
  getEtaMs,
  type ProcessingJob,
} from '@/hooks/papers/usePaperProcessingStatus';
import { useEffect, useMemo, useState } from 'react';
import { AIObjectionNotice } from '@/components/ai/AIObjectionNotice';
import { AI_OBJECTION_COPY } from '@/lib/ai/objectionNotice';

interface ProcessingTimelineProps {
  paperId: string;
  variant?: 'full' | 'sticky';
  className?: string;
  /** When all jobs finish, hide the timeline after this delay (ms). 0 = never auto-hide. */
  autoHideAfterCompleteMs?: number;
}

function formatEta(ms: number): string {
  const s = Math.max(1, Math.round(ms / 1000));
  if (s < 60) return `~${s}s`;
  return `~${Math.round(s / 60)}m`;
}

function elapsed(job: ProcessingJob): string {
  if (!job.started_at) return '';
  const start = new Date(job.started_at).getTime();
  const end = job.finished_at ? new Date(job.finished_at).getTime() : Date.now();
  const s = Math.max(1, Math.round((end - start) / 1000));
  if (s < 60) return `${s}s`;
  return `${Math.round(s / 60)}m`;
}

function StatusIcon({ job }: { job: ProcessingJob }) {
  switch (job.status) {
    case 'completed':
      return <CircleCheck className="h-4 w-4 text-emerald-500" aria-label="Completed" />;
    case 'failed':
      return <CircleAlert className="h-4 w-4 text-destructive" aria-label="Failed" />;
    case 'skipped':
      return <CircleSlash className="h-4 w-4 text-muted-foreground" aria-label="Skipped" />;
    case 'waiting_for_credits':
      return <Wallet className="h-4 w-4 text-amber-500" aria-label="Waiting for credits" />;
    case 'running':
      return <Loader2 className="h-4 w-4 text-primary animate-spin" aria-label="Running" />;
    default:
      return <Circle className="h-4 w-4 text-muted-foreground" aria-label="Pending" />;
  }
}

export function ProcessingTimeline({
  paperId,
  variant = 'full',
  className,
  autoHideAfterCompleteMs = 0,
}: ProcessingTimelineProps) {
  const { orderedJobs, isProcessing, hasErrors, isComplete, retry } = usePaperProcessingStatus(paperId);
  const [hidden, setHidden] = useState(false);

  // Tick every second so ETAs/elapsed update smoothly while running.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isProcessing) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [isProcessing]);

  useEffect(() => {
    if (!autoHideAfterCompleteMs) return;
    if (isComplete && !hasErrors) {
      const id = window.setTimeout(() => setHidden(true), autoHideAfterCompleteMs);
      return () => window.clearTimeout(id);
    }
  }, [isComplete, hasErrors, autoHideAfterCompleteMs]);

  const summary = useMemo(() => {
    if (orderedJobs.length === 0) return '';
    if (hasErrors) return 'Some steps need attention';
    if (isComplete) return 'All set';
    const running = orderedJobs.find((j) => j.status === 'running');
    return running ? STEP_LABELS[running.step] : 'Preparing…';
  }, [orderedJobs, hasErrors, isComplete]);

  if (hidden || orderedJobs.length === 0) return null;

  const isSticky = variant === 'sticky';

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'rounded-xl border bg-card text-card-foreground',
        isSticky ? 'p-3 shadow-sm' : 'p-5',
        className,
      )}
    >
      {isSticky && (
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            {isProcessing ? (
              <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />
            ) : (
              <CircleCheck className="h-3.5 w-3.5 text-emerald-500" />
            )}
            <span>{summary}</span>
          </div>
        </div>
      )}

      {!isSticky && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold tracking-tight">
            {isComplete ? 'Your paper is ready' : 'Preparing your paper'}
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            {isComplete
              ? 'Every step finished successfully.'
              : 'You can leave this page — we keep working in the background.'}
          </p>
        </div>
      )}

      <ul className={cn('space-y-2', isSticky && 'space-y-1.5')}>
        {orderedJobs.map((job) => {
          const isRunning = job.status === 'running';
          const isPending = job.status === 'pending';
          const eta = isPending || isRunning ? formatEta(getEtaMs(job.step)) : '';
          return (
            <li
              key={job.id}
              className={cn(
                'flex items-center gap-3 text-sm',
                isPending && 'text-muted-foreground',
              )}
            >
              <StatusIcon job={job} />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="truncate">{STEP_LABELS[job.step]}</span>
                  <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                    {job.status === 'completed' && elapsed(job)}
                    {job.status === 'running' && elapsed(job)}
                    {job.status === 'pending' && eta}
                    {job.status === 'failed' && 'failed'}
                    {job.status === 'skipped' && 'skipped'}
                    {job.status === 'waiting_for_credits' && 'on hold'}
                  </span>
                </div>
                {isRunning && (
                  <div className="mt-1.5 h-1 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-primary transition-[width] duration-700 ease-out"
                      style={{ width: `${Math.max(5, job.progress)}%` }}
                    />
                  </div>
                )}
                {isRunning && job.message && !isSticky && (
                  <p className="text-xs text-muted-foreground mt-1 truncate">{job.message}</p>
                )}
                {job.status === 'skipped' && job.message && !isSticky && (
                  job.message === AI_OBJECTION_COPY.jobMessage ? (
                    <AIObjectionNotice variant="inline" className="mt-1" />
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1">{job.message}</p>
                  )
                )}
                {job.status === 'waiting_for_credits' && !isSticky && (
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      {job.message || 'Waiting for credits.'} Your paper is saved and nothing was charged.
                    </p>
                    <Link
                      to="/pricing"
                      className="text-xs font-medium text-primary hover:underline"
                    >
                      Add credits
                    </Link>
                  </div>
                )}
                {job.status === 'failed' && (
                  <div className="flex items-center gap-2 mt-1">
                    {job.error && (
                      <p className="text-xs text-destructive truncate">{job.error}</p>
                    )}
                    {job.step !== 'search_indexing' && (
                      <button
                        type="button"
                        onClick={() => retry(job.step)}
                        className="text-xs font-medium text-primary hover:underline"
                      >
                        Retry
                      </button>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
